// Base interface and class for all effects
import { Easing as RemotionEasing } from 'remotion';

export type EasingFunction = (t: number) => number;

export interface CanvasDimensions {
    resolutionWidth: number;  // Canvas resolution width
    resolutionHeight: number; // Canvas resolution height
    elementWidth: number;     // Actual canvas element width
    elementHeight: number;    // Actual canvas element height
}

export interface Effect {
    type: string;
    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult;
    calculateProgress(currentTime: number): number;
}

// Re-export Remotion's easing functions with backward compatibility
export const Easing = {
    // Linear
    linear: (t: number) => RemotionEasing.linear(t),
    
    // Quadratic
    easeInQuad: (t: number) => RemotionEasing.in(RemotionEasing.quad)(t),
    easeOutQuad: (t: number) => RemotionEasing.out(RemotionEasing.quad)(t),
    easeInOutQuad: (t: number) => RemotionEasing.inOut(RemotionEasing.quad)(t),
    
    // Cubic
    easeInCubic: (t: number) => RemotionEasing.in(RemotionEasing.cubic)(t),
    easeOutCubic: (t: number) => RemotionEasing.out(RemotionEasing.cubic)(t),
    easeInOutCubic: (t: number) => RemotionEasing.inOut(RemotionEasing.cubic)(t),
    
    // Sine (smooth, organic curves)
    easeInSine: (t: number) => RemotionEasing.in(RemotionEasing.sin)(t),
    easeOutSine: (t: number) => RemotionEasing.out(RemotionEasing.sin)(t),
    easeInOutSine: (t: number) => RemotionEasing.inOut(RemotionEasing.sin)(t),
    
    // Quart (strong but not extreme)
    easeInQuart: (t: number) => RemotionEasing.in(RemotionEasing.poly(4))(t),
    easeOutQuart: (t: number) => RemotionEasing.out(RemotionEasing.poly(4))(t),
    easeInOutQuart: (t: number) => RemotionEasing.inOut(RemotionEasing.poly(4))(t),
    
    // Quint (stronger curves)
    easeInQuint: (t: number) => RemotionEasing.in(RemotionEasing.poly(5))(t),
    easeOutQuint: (t: number) => RemotionEasing.out(RemotionEasing.poly(5))(t),
    easeInOutQuint: (t: number) => RemotionEasing.inOut(RemotionEasing.poly(5))(t),
    
    // Expo (dramatic, exponential)
    easeInExpo: (t: number) => RemotionEasing.in(RemotionEasing.exp)(t),
    easeOutExpo: (t: number) => RemotionEasing.out(RemotionEasing.exp)(t),
    easeInOutExpo: (t: number) => RemotionEasing.inOut(RemotionEasing.exp)(t),
    
    // Circ (circular, smooth curves)
    easeInCirc: (t: number) => RemotionEasing.in(RemotionEasing.circle)(t),
    easeOutCirc: (t: number) => RemotionEasing.out(RemotionEasing.circle)(t),
    easeInOutCirc: (t: number) => RemotionEasing.inOut(RemotionEasing.circle)(t),
    
    // Back (overshoot effects)
    easeInBack: (t: number) => RemotionEasing.in(RemotionEasing.back())(t),
    easeOutBack: (t: number) => RemotionEasing.out(RemotionEasing.back())(t),
    easeInOutBack: (t: number) => RemotionEasing.inOut(RemotionEasing.back())(t),
    
    // Spring-like using Remotion's elastic
    spring: (t: number) => RemotionEasing.out(RemotionEasing.elastic())(t),
    
    // Bounce using Remotion's bounce
    bounce: (t: number) => RemotionEasing.out(RemotionEasing.bounce)(t)
};

export type EffectShape = 'rect' | 'circle';

export interface EffectResult {
    shape: EffectShape;
    rect?: { x: number, y: number, width: number, height: number };
    circle?: { x: number, y: number, radius: number };
}

// Layer types that can be targeted by multi-track effects
export type LayerType = 'background' | 'scene' | 'overlay' | 'overlayTransition' | 'textOverlay' | 'caption' | 'branding';

// Interface for effects that can target multiple layers simultaneously
export interface MultiTrackEffect extends Effect {
    targetLayers: LayerType[];
    excludeLayers?: LayerType[];
    containerBased: boolean; // Whether effect should be applied to a container wrapping the layers (true) or per-layer (false)
    getContainerStyle(progress: number, dimensions?: CanvasDimensions, effectResult?: EffectResult): React.CSSProperties;
    shouldApplyToLayer(layerType: LayerType): boolean;
}

export abstract class BaseEffect implements Effect {
    type: string;
    duration: number;
    at?: number;
    easing: EasingFunction;
    containerEffect: boolean;

    constructor(
        type: string, 
        duration: number, 
        at?: number, 
        easing: EasingFunction = Easing.linear,
        containerEffect: boolean = false
    ) {
        this.type = type;
        this.duration = duration;
        this.at = at;
        this.easing = easing;
        this.containerEffect = containerEffect;
    }

    abstract apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult;

    // New method for container effects
    getContainerStyle?(
        progress: number,
        dimensions?: CanvasDimensions,
        effectResult?: EffectResult
    ): React.CSSProperties {
        return {};
    }

    calculateProgress(currentTime: number): number {
        // Validate input
        if (typeof currentTime !== 'number' || isNaN(currentTime)) {
            return 0;
        }
        
        // Check if at is undefined or null, but allow 0
        if (this.at === undefined || this.at === null) {
            return 0;
        }
        
        // Validate duration
        if (typeof this.duration !== 'number' || isNaN(this.duration) || this.duration <= 0) {
            return 0;
        }
        
        const startTime = this.at;
        const endTime = startTime + this.duration;
        
        if (currentTime < startTime) {
            return 0;
        }
        if (currentTime > endTime) {
            return 1;
        }
        
        // Calculate linear progress
        const linearProgress = (currentTime - startTime) / this.duration;
        
        // Validate linear progress
        if (typeof linearProgress !== 'number' || isNaN(linearProgress)) {
            return 0;
        }
        
        // Apply easing function
        const easedProgress = this.easing(linearProgress);
        
        // Validate eased progress
        if (typeof easedProgress !== 'number' || isNaN(easedProgress)) {
            return linearProgress; // Fall back to linear progress
        }
        
        return easedProgress;
    }
} 